import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  useTheme,
  Container,
  IconButton,
  Icon,
  Menu,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import logo from "../../../../assets/logo/text-logo.png";

const navItems = [
  { label: "Home", path: "/" },
  { label: "Features", path: "/features" },
  { label: "Solutions", path: "/solutions" },
  // { label: "Pricing", path: "/pricing" },
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
];

export const MainNavbar: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(
    null
  );

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        background: "linear-gradient(135deg, #000B18 0%, #001E3C 100%)",
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        py: 1,
        fontFamily: "Montserrat",
      }}
    >
      <Container maxWidth="lg">
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: 64, md: 72 },
          }}
        >
          <Box
            onClick={() => navigate("/")}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              "&:hover": {
                opacity: 0.9,
              },
              transition: "opacity 0.2s ease",
            }}
          >
            <img
              src={logo}
              alt="VenFlows"
              style={{
                height: isMobile ? "32px" : "40px",
                width: "auto",
                objectFit: "contain",
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, md: 3 },
              ml: "auto",
            }}
          >
            {!isMobile &&
              navItems.map((item) => (
                <Button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    fontWeight: 500,
                    position: "relative",
                    color: "rgba(255, 255, 255, 0.8)",
                    opacity: location.pathname === item.path ? 1 : 0.7,
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: -2,
                      left: 8,
                      right: 8,
                      height: 2,
                      borderRadius: 1,
                      bgcolor: "#06B6D4",
                      transform:
                        location.pathname === item.path
                          ? "scaleX(1)"
                          : "scaleX(0)",
                      transition: "transform 0.3s ease",
                      transformOrigin: "left",
                    },
                    "&:hover": {
                      opacity: 1,
                      color: "white",
                      "&::after": {
                        transform: "scaleX(1)",
                      },
                    },
                    transition: "all 0.3s ease",
                    fontFamily: "Montserrat",
                  }}
                >
                  {item.label}
                </Button>
              ))}

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => navigate("/auth/login")}
                sx={{
                  display: { xs: "none", md: "inline-flex" },
                  borderWidth: 1.5,
                  px: 3,
                  fontWeight: 600,
                  fontFamily: "Montserrat",
                  color: "white",
                  borderColor: "rgba(255, 255, 255, 0.2)",
                  "&:hover": {
                    borderWidth: 1.5,
                    transform: "translateY(-1px)",
                    borderColor: "#06B6D4",
                    boxShadow: "0 4px 12px rgba(6, 182, 212, 0.2)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Login
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate("/auth/register")}
                sx={{
                  display: { xs: "none", md: "inline-flex" },
                  px: 3,
                  fontWeight: 600,
                  fontFamily: "Montserrat",
                  background:
                    "linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%)",
                  border: "1px solid rgba(6, 182, 212, 0.5)",
                  color: "white",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    background:
                      "linear-gradient(135deg, rgba(6, 182, 212, 0.25) 0%, rgba(59, 130, 246, 0.25) 100%)",
                    boxShadow: "0 4px 12px rgba(6, 182, 212, 0.4)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Get Started
              </Button>
            </Box>

            {isMobile && (
              <>
                <IconButton
                  onClick={handleMobileMenuOpen}
                  sx={{
                    color: "white",
                  }}
                >
                  <Icon>menu</Icon>
                </IconButton>
                <Menu
                  anchorEl={mobileMenuAnchor}
                  open={Boolean(mobileMenuAnchor)}
                  onClose={handleMobileMenuClose}
                  sx={{
                    "& .MuiPaper-root": {
                      bgcolor: "#000B18",
                      minWidth: 200,
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  {navItems.map((item) => (
                    <MenuItem
                      key={item.path}
                      onClick={() => {
                        navigate(item.path);
                        handleMobileMenuClose();
                      }}
                      sx={{
                        color: "white",
                        fontFamily: "Montserrat",
                        fontWeight: location.pathname === item.path ? 600 : 400,
                        opacity: location.pathname === item.path ? 1 : 0.7,
                        "&:hover": {
                          bgcolor: "rgba(6, 182, 212, 0.1)",
                        },
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};
